<script lang="ts">
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';

	import { convertDateFormat } from 'src/helpers/date_helper';
	import { paypal, paypalTest } from 'src/services/http';

	import { android_version, app, os } from 'src/stores/session/native-bridge';

	import compareVersions from 'compare-versions';
	import {
		Alert,
		Button,
		IconButton,
		Text,
		ExpandableTable,
		ExpandableTableRow,
		ExpandableTableCol,
		Loader,
	} from '@Shelly/shelly-design-system';

	import type { InvoiceData } from 'src/types/InvoicesList';
	import { isDevServerUser } from 'src/helpers/user_server_conditions';

	const colWidths = ['1fr', '1fr', '1fr', '48px'];
	const isAndroidApp = get(app) && get(os) !== 'IOS';

	let sortedInvoicesList: InvoiceData[] = [];
	let latestInvoice: InvoiceData;
	let initialLoading = false;
	let loadingID = '';

	const blobToBase64 = (blob: Blob): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(blob);
		});

	onMount(async () => {
		initialLoading = true;

		let obj: Record<number, { amount: { currency_code: string; value: string }; time: string }> = {};
		try {
			let res: any;
			if (isDevServerUser(['repo', 'nb'])) {
				res = await paypalTest.get('/api/invoice/list');
			} else {
				res = await paypal.get('/api/invoice/list');
			}

			for (const [key, value] of Object.entries(res)) {
				Object.assign(obj, { [key]: JSON.parse(value as any) });
			}
		} catch (error) {
			console.log(error);
		}

		sortedInvoicesList = Object.keys(obj)
			.map((id) => {
				return { id, ...obj[id] };
			})
			.sort((a, b) => {
				return +new Date(b.time) - +new Date(a.time);
			});

		initialLoading = false;
	});

	const getInvoiceById = async (id) => {
		try {
			loadingID = id;
			const invoiceNumber =
				sortedInvoicesList.find((invoice) => invoice.id === id)?.display_id ?? id;
			const a = document.createElement('a');
			const filename = `invoice_${invoiceNumber}.pdf`;

			let res: any;
			if (isDevServerUser(['repo', 'nb'])) {
				res = await paypalTest.get(`/api/invoice/${id}`);
			} else {
				res = await paypal.get(`/api/invoice/${id}`, {
					headers: { responseType: 'blob' },
				});
			}

			if (isAndroidApp) {
				const base64Pdf = await blobToBase64(res);
				a.setAttribute(
					'href',
					`data:application/octet-stream;filename=${filename};base64,${base64Pdf}`,
				);
				a.setAttribute('download', filename);
				a.click();
			} else {
				a.setAttribute('target', '_blank');
				a.setAttribute('download', filename);
				const windowURL = window.webkitURL || window.URL,
					url = windowURL.createObjectURL(res);
				a.setAttribute('href', url);
				a.click();
				windowURL.revokeObjectURL(url);
			}
			loadingID = '';
		} catch (error) {
			console.log(error);
		}
	};

	const getLatestInvoice = () => {
		latestInvoice = sortedInvoicesList[0];
		getInvoiceById(latestInvoice.id);
	};

	const removeSeconds = (time) => {
		let convertedTime = convertDateFormat(time);
		return convertedTime.replace(convertedTime.match(/\d*:\d*(:\d*)/)[1], '');
	};

	let androidPDFDownloadFix = false;
	const isApp = $app;

	if (isApp) {
		const isIOS = $os === 'IOS';
		const androidVersion = $android_version;
		const minVersion = '1.19.1';

		androidPDFDownloadFix = isIOS;
		try {
			if (compareVersions.compare(androidVersion, minVersion, '>=')) {
				androidPDFDownloadFix = true;
			}
		} catch (error) {
			console.error('we could not compare versions');
		}
	} else {
		androidPDFDownloadFix = true;
	}
</script>

{#if initialLoading}
	<Loader size="l" class="d-flex justify-content-center" />
{:else if sortedInvoicesList.length === 0}
	<Alert heading={$format('no_invoice_history', { default: 'No Invoice History' })} />
{:else}
	<Button
		class="mb-s"
		size="m"
		loading={latestInvoice?.id === loadingID}
		disabled={latestInvoice?.id === loadingID || !androidPDFDownloadFix}
		on:click={getLatestInvoice}
	>
		{$format('download_latest_invoice', {
			default: 'Download Latest Invoice',
		})}
	</Button>

	<ExpandableTable {colWidths} isSimple>
		<ExpandableTableRow heading notExpandable>
			<ExpandableTableCol>
				<Text styleType="h2">{$format('invoice_history_invoice', { default: 'Invoice' })}</Text>
			</ExpandableTableCol>
			<ExpandableTableCol>
				<Text styleType="h2">{$format('invoice_history_date', { default: 'Date' })}</Text>
			</ExpandableTableCol>
			<ExpandableTableCol>
				<Text styleType="h2">{$format('invoice_history_total', { default: 'Total' })}</Text>
			</ExpandableTableCol>
			<ExpandableTableCol />
		</ExpandableTableRow>

		{#each sortedInvoicesList as invoicesList}
			<ExpandableTableRow notExpandable>
				<ExpandableTableCol>
					<Text styleType="body-small">{invoicesList.display_id}</Text>
				</ExpandableTableCol>
				<ExpandableTableCol>
					<Text styleType="body-small">
						{removeSeconds(invoicesList.time)}
					</Text>
				</ExpandableTableCol>
				<ExpandableTableCol>
					<Text styleType="body-small">
						{`${invoicesList.amount.value} ${invoicesList.amount.currency_code}`}
					</Text>
				</ExpandableTableCol>

				<ExpandableTableCol>
					<IconButton
						aria-label={$format('button_download_invoice', {
							default: 'Download invoice',
						})}
						icon="fas fa-file-arrow-down"
						variant="secondary"
						loading={loadingID === invoicesList.id}
						disabled={loadingID === invoicesList.id || !androidPDFDownloadFix}
						on:click={() => getInvoiceById(invoicesList.id)}
					/>
				</ExpandableTableCol>
			</ExpandableTableRow>
		{/each}
	</ExpandableTable>
{/if}
